import { haversineKm } from '../data/enrichment.js';

/**
 * "Find my Plane" — MVP/Studie.
 *
 * Zweite Seite des Info-Panels: zeigt einen Pfeil, der in die Richtung des
 * ausgewählten Flugzeugs zeigt, plus Entfernung. Nutzt Geräte-Standort
 * (Geolocation) und – wenn verfügbar – den Geräte-Kompass
 * (DeviceOrientation), sodass der Pfeil relativ zur Blickrichtung dreht.
 *
 * Ohne Kompass zeigt der Pfeil die absolute Peilung (Norden = oben).
 */
export class FindMyPlane {
  constructor() {
    this.arrow = document.getElementById('fmpArrow');
    this.directionEl = document.getElementById('fmpDirection');
    this.distanceEl = document.getElementById('fmpDistance');
    this.enableBtn = document.getElementById('fmpEnable');
    this.hintEl = document.getElementById('fmpHint');

    this.userPos = null;       // { lat, lon }
    this.deviceHeading = null; // Grad, 0 = Norden (relativ zur Blickrichtung)
    this.flight = null;        // { lat, lon }
    this.active = false;       // Seite gerade sichtbar?
    this.started = false;      // Sensoren bereits angefragt?

    this.enableBtn.addEventListener('click', () => this.enable());
  }

  /** Aktuelle Flugposition (bei jedem Poll aufgerufen). */
  update(flight) {
    this.flight = flight
      ? { lat: flight.position.lat, lon: flight.position.lon }
      : null;
    if (this.active) this.render();
  }

  /** Seite wurde sichtbar. */
  activate() {
    this.active = true;
    this.render();
  }

  /** Standort + Kompass anfordern (braucht User-Geste, v.a. auf iOS). */
  enable() {
    if (this.started) return;
    this.started = true;
    this.startGeolocation();
    this.startOrientation();
  }

  startGeolocation() {
    if (!('geolocation' in navigator)) {
      this.hint('Standort auf diesem Gerät nicht verfügbar.');
      return;
    }
    navigator.geolocation.watchPosition(
      (p) => {
        this.userPos = { lat: p.coords.latitude, lon: p.coords.longitude };
        this.render();
      },
      () => this.hint('Standortzugriff verweigert.'),
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );
  }

  startOrientation() {
    const onOrient = (e) => {
      let h = null;
      if (typeof e.webkitCompassHeading === 'number') {
        // iOS: bereits als Kompass-Heading (0 = Norden)
        h = e.webkitCompassHeading;
      } else if (typeof e.alpha === 'number') {
        // Sonst aus alpha ableiten (nur bei absoluter Ausrichtung sinnvoll)
        h = (360 - e.alpha) % 360;
      }
      if (h != null && !Number.isNaN(h)) {
        this.deviceHeading = h;
        this.render();
      }
    };

    const DOE = window.DeviceOrientationEvent;
    if (DOE && typeof DOE.requestPermission === 'function') {
      // iOS 13+
      DOE.requestPermission()
        .then((state) => {
          if (state === 'granted') {
            window.addEventListener('deviceorientation', onOrient, true);
          } else {
            this.hint('Kompasszugriff verweigert — Pfeil zeigt nach Norden.');
          }
        })
        .catch(() => this.hint('Kompass nicht verfügbar — Pfeil zeigt nach Norden.'));
    } else if ('ondeviceorientationabsolute' in window) {
      window.addEventListener('deviceorientationabsolute', onOrient, true);
    } else if (DOE) {
      window.addEventListener('deviceorientation', onOrient, true);
    } else {
      this.hint('Kompass nicht verfügbar — Pfeil zeigt nach Norden.');
    }
  }

  hint(msg) {
    if (this.hintEl) this.hintEl.textContent = msg;
  }

  render() {
    if (!this.flight) {
      this.directionEl.textContent = '—';
      this.distanceEl.textContent = 'Kein Flug ausgewählt';
      return;
    }
    if (!this.userPos) {
      this.directionEl.textContent = '—';
      this.distanceEl.textContent = 'Noch nicht verfügbar';
      return;
    }

    const bearing = (bearingDeg(this.userPos, this.flight) + 360) % 360;
    const dist = haversineKm(this.userPos, this.flight);
    const heading = this.deviceHeading ?? 0;
    const relative = ((bearing - heading) % 360 + 360) % 360;

    this.arrow.style.transform = `rotate(${relative}deg)`;
    this.directionEl.textContent = `${cardinal(bearing)} · ${Math.round(bearing)}°`;
    this.distanceEl.textContent =
      dist < 1 ? `${Math.round(dist * 1000)} m entfernt` : `${dist.toFixed(1)} km entfernt`;

    this.enableBtn.classList.add('hidden');
    if (this.deviceHeading == null) {
      this.hint('Norden ist oben (Gerät ohne Kompass-Sensor).');
    } else {
      this.hint('Halte dein Gerät flach — der Pfeil zeigt zum Flugzeug.');
    }
  }
}

/** Großkreis-Peilung von a nach b in Grad (0 = Norden). */
function bearingDeg(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const Δλ = toRad(b.lon - a.lon);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return (Math.atan2(y, x) * 180) / Math.PI;
}

/** Deutsche Himmelsrichtung (8-Punkt) aus einer Peilung. */
function cardinal(deg) {
  const dirs = ['Norden', 'Nordosten', 'Osten', 'Südosten', 'Süden', 'Südwesten', 'Westen', 'Nordwesten'];
  return dirs[Math.round(((deg % 360) + 360) % 360 / 45) % 8];
}
